// @flow
import {type SagaIterator} from 'redux-saga';
import {takeEvery, select, put, putResolve, call, take} from 'redux-saga/effects';
import {CATEGORY, GENERAL, PDP, sendMobileAnalyticsEvent} from '@optimaxdev/analytics/mobile';

import type {SimpleProductType} from 'constants/flow/flowTypesProduct';
import {getCurrentGender} from 'selectors/gender/gender';
import {setTryOnVisibility} from 'actions/tryOnVisibility/tryOnVisibility';
import {getCloudinaryURL} from 'libs/cloudinary/cloudinary';
import {GENDER} from 'constants/actions';
import {isCategoryPage as isCategoryPageSelector} from 'selectors/routerInfo/routerInfo';
import {getPage} from 'selectors/pageApp/pageApp';
import {sendEventToDataLayerForTryOn} from 'libs/analytics/sendAnalytics';

import {handlerUpdateFaceSettings, updateFaceSettings} from '../reducers/settings';
import {uploadCroppedImage} from '../reducers/photo';
import {
    setProductInfo,
    fetchSliderFaces,
    setCurrentStep,
    type TryOnFaceType,
} from '../reducers/collection';
import {STEPS} from '../constants/constants';
import {
    getUploadedPhoto,
    getActiveProduct,
    getColors,
    isTryOnView,
    getTryOnCollection,
    getTryOnFaces,
    getCurrentFace,
} from '../selectors/tryOn';
import {fetchProduct, setProduct} from '../reducers/product';
import {setTryOnViewShow, updateTryOnViewFaces} from '../reducers/view/view';

/**
 * Send event with default model to dataLayer for tryOn on wishlist, category page or PDP
 */
export function* sendAnalyticsForTryOnOtherPlaces(): SagaIterator {
    const pageName = yield select(getPage);
    const currentModel = yield select(getCurrentFace);
    sendEventToDataLayerForTryOn(currentModel, pageName, {isDefault: true});
}

/**
 * Get product info from local store or fetch remote
 */
export function* getProduct(): SagaIterator {
    const {productInfo} = yield select(getTryOnCollection);
    const product = yield select(getActiveProduct);

    if (product) {
        yield putResolve(setProduct(product));
    } else {
        yield putResolve(fetchProduct(productInfo.id));
    }

    yield call(sendAnalyticsForTryOnOtherPlaces);

    const colors = yield select(getColors);
    colors.forEach((color: SimpleProductType) => {
        new Image().src = getCloudinaryURL(color.color_image);
        new Image().src = getCloudinaryURL(color.frame_url);
    });
}

/**
 * Get faces and frame from backend
 */
export function* fetchFaces(): SagaIterator {
    const {genderKey} = yield select(getCurrentGender);

    yield put(fetchSliderFaces(genderKey));

    const faces = yield select(getTryOnFaces);

    faces.forEach((face: TryOnFaceType) => {
        new Image().src = face.url;
    });
}

/**
 * Get faces and frame from backend on tryOnView
 */
export function* fetchFaceOnTryOnView(): SagaIterator {
    // @TODO GUSA-8518
    yield take(GENDER.SET);
    yield call(fetchFaces);
}

/**
 * Get faces and frame with product from backend
 */
export function* fetchFacesWithProduct(): SagaIterator {
    yield call(getProduct);
    yield call(fetchFaces);
}

/**
 * Update try on photo frame settings.
 */
export function* updateSettingsPhotoFaces(): SagaIterator {
    const collection = yield select(getTryOnCollection);
    const {genderKey: gender} = yield select(getCurrentGender);
    const photo = yield select(getUploadedPhoto);
    yield putResolve(uploadCroppedImage(photo.croppedImage));
    yield putResolve(updateFaceSettings(collection, photo, gender === 'unisex' ? 'women' : gender));
}

/**
 * Update try on photo frame settings.
 */
export function* updateSettingsTryOnView(): SagaIterator {
    yield call(updateSettingsPhotoFaces);
    yield call(fetchFaces);
    yield putResolve(setTryOnVisibility(false));
    yield putResolve(setTryOnViewShow(false));
}

/**
 * Update try on photo frame settings.
 */
export function* updateSettings(): SagaIterator {
    const onTryOnView = yield select(isTryOnView);
    const isCategoryPage = yield select(isCategoryPageSelector);
    if (onTryOnView && isCategoryPage) {
        yield call(updateSettingsTryOnView);
    } else {
        yield call(updateSettingsPhotoFaces);
        yield put(setCurrentStep(STEPS.RESULT));
        yield call(fetchFacesWithProduct);
    }
}

/**
 * Send the event after a successful image upload
 */
export function* sendAnalyticsSaga(): SagaIterator {
    const eventNameForTryOn = {
        wishlist: GENERAL.WISHLIST_PRODUCT,
        category: CATEGORY.TRY_ON_POPUP,
        product: PDP.WISHLIST_USAGE,
    };
    const isLoadFromCategory = yield select(isTryOnView);
    const pageName = yield select(getPage);
    yield call(
        sendMobileAnalyticsEvent,
        isLoadFromCategory ? CATEGORY.TOP_MENU : eventNameForTryOn[pageName],
        {
            label: 'Success - Picture Uploaded',
        },
    );
}

/**
 *
 * Makes try on visible
 */
export function* makeTryOnVisible(): SagaIterator {
    yield put(setTryOnVisibility(true));
}

/**
 * Try On root saga
 */
export function* tryOnSaga(): SagaIterator {
    yield takeEvery(setProductInfo, fetchFacesWithProduct);
    yield takeEvery(handlerUpdateFaceSettings, updateSettings);
    yield takeEvery(updateTryOnViewFaces, fetchFaceOnTryOnView);
    yield takeEvery(`${uploadCroppedImage}_FULFILLED`, sendAnalyticsSaga);
    yield takeEvery(setProductInfo, makeTryOnVisible);
}
